import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Where everybody is, drawn over the room's walls.
 *
 * TWO SOURCES, and the split is the point. The walls come from the map the
 * client already received in WELCOME, so the room outline costs nothing extra.
 * The dots come from the OVERVIEW feed, which is the one payload in this game
 * that ignores interest management — every other message sends you only what is
 * near you, which is exactly why a map built from them would show your own
 * corner and nothing else.
 *
 * Canvas rather than DOM nodes: a large map is thousands of wall cells redrawn
 * whenever anybody moves.
 */

/**
 * One place for the palette, so adding friends later is a line here and a kind
 * on the payload — not a refactor.
 */
const DOT = {
  self: '#F5D97A',
  player: '#4CAFD4',
  npc: '#8BD44C',
};

const WALL = '#374151';
const FLOOR = '#111827';
/** Buildings. Distinct from walls so a town reads as a plan rather than as
 *  scattered geometry when the whole map is on screen. */
const ZONE = '#2b2340';

function Minimap({
  map, overview, localShortId, large, onWalkTo,
}) {
  const canvasRef = useRef(null);
  // The draw transform, kept so a click can be converted with exactly the same
  // numbers the dots were drawn with — recomputing it would be two places to
  // get the letterboxing offsets right.
  const viewRef = useRef(null);
  /**
   * The walls and buildings, drawn once onto an offscreen canvas.
   *
   * The map does not change while you are standing in it, but `overview`
   * arrives at OVERVIEW_HZ — so redrawing every solid tile to move a few dots
   * meant one `fillRect` per solid tile, twice a second, forever. On the 80x60
   * world that was ~7,000 a second; on a 160x120 city it is ~26,000, and nearly
   * four times that with the large map open.
   *
   * Cached on the map itself rather than on its size, so this survives a
   * republish for the right reason: new geometry is a new picture.
   */
  const backdropRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !map) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return; // jsdom, and browsers without 2D — draw nothing rather than throw

    // `walls` is the layer collision is derived from server-side, so drawing it
    // shows exactly what you cannot walk through.
    const walls = map.layers?.find((l) => l.name === 'walls')?.data;
    const { width, height } = map;
    if (!walls || !width || !height) return;

    const scale = Math.min(canvas.width / width, canvas.height / height);
    const ox = (canvas.width - width * scale) / 2;
    const oy = (canvas.height - height * scale) / 2;
    viewRef.current = { scale, ox, oy };

    /**
     * Keyed on the map OBJECT, not its name. A republish keeps the name and
     * changes the geometry, so a name-based key would leave the old walls on
     * screen after an edit went live. Every WELCOME hands over a fresh object,
     * which makes identity exactly the right test.
     */
    const cached = backdropRef.current;
    if (!cached || cached.map !== map
      || cached.w !== canvas.width || cached.h !== canvas.height) {
      // `document.createElement` rather than OffscreenCanvas: jsdom has the
      // former, and the engine already builds recolour canvases this way.
      const backdrop = document.createElement('canvas');
      backdrop.width = canvas.width;
      backdrop.height = canvas.height;
      const bx = backdrop.getContext('2d');

      bx.fillStyle = FLOOR;
      bx.fillRect(ox, oy, width * scale, height * scale);

      // Buildings under the walls, so a zone reads as a filled block and its
      // generated perimeter still draws on top as wall.
      bx.fillStyle = ZONE;
      (map.zones || []).forEach((z) => {
        bx.fillRect(
          ox + (z.x / map.tileSize) * scale,
          oy + (z.y / map.tileSize) * scale,
          (z.width / map.tileSize) * scale,
          (z.height / map.tileSize) * scale,
        );
      });

      bx.fillStyle = WALL;
      for (let i = 0; i < walls.length; i += 1) {
        if (walls[i] !== 0) {
          const tx = i % width;
          const ty = Math.floor(i / width);
          // +1 closes the hairline seams rounding leaves between adjacent cells
          // at fractional scale.
          bx.fillRect(ox + tx * scale, oy + ty * scale, scale + 1, scale + 1);
        }
      }

      backdropRef.current = {
        map, w: canvas.width, h: canvas.height, backdrop,
      };
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(backdropRef.current.backdrop, 0, 0);

    // Dots last, so nothing is drawn over somebody. Radius is floored at 1.5 so
    // a character stays visible on a large map drawn small.
    const radius = Math.max(1.5, scale * 0.9);
    const dot = (entry, colour) => {
      const [, tx, ty] = entry;
      ctx.beginPath();
      ctx.arc(ox + (tx + 0.5) * scale, oy + (ty + 0.5) * scale, radius, 0, Math.PI * 2);
      ctx.fillStyle = colour;
      ctx.fill();
    };

    (overview?.n || []).forEach((e) => dot(e, DOT.npc));
    (overview?.p || []).forEach((e) => dot(e, e[0] === localShortId ? DOT.self : DOT.player));

    // Your own dot again, ringed, so it is findable in a crowd.
    const me = (overview?.p || []).find((e) => e[0] === localShortId);
    if (me) {
      ctx.beginPath();
      ctx.arc(ox + (me[1] + 0.5) * scale, oy + (me[2] + 0.5) * scale, radius + 2, 0, Math.PI * 2);
      ctx.strokeStyle = DOT.self;
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }, [map, overview, localShortId]);

  const size = large ? { width: 520, height: 380 } : { width: 200, height: 150 };

  return (
    <div
      className={`pointer-events-auto rounded-lg border border-gray-800/80 bg-gray-950/85 p-1.5 backdrop-blur-sm ${
        large ? 'shadow-2xl' : ''
      }`}
    >
      <canvas
        ref={canvasRef}
        width={size.width}
        height={size.height}
        onClick={(event) => {
          const view = viewRef.current;
          if (!view || !onWalkTo) return;

          // Canvas pixels, then map tiles, then foot-space world units — the
          // same space the pathfinder and collision both work in.
          const rect = event.currentTarget.getBoundingClientRect();
          const cx = ((event.clientX - rect.left) / rect.width) * event.currentTarget.width;
          const cy = ((event.clientY - rect.top) / rect.height) * event.currentTarget.height;

          onWalkTo({
            x: ((cx - view.ox) / view.scale) * map.tileSize,
            y: ((cy - view.oy) / view.scale) * map.tileSize,
          });
        }}
        className="block cursor-pointer"
      />
      <div className="mt-1 flex items-center gap-2 px-0.5 text-[10px] text-gray-500">
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: DOT.self }} />
          you
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: DOT.player }} />
          players
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: DOT.npc }} />
          NPCs
        </span>
        <span className="ml-auto">{large ? 'M small · Shift+M close' : 'Shift+M large'}</span>
      </div>
    </div>
  );
}

Minimap.propTypes = {
  /** The map from WELCOME — already on the client, so the walls are free. */
  map: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
    tileSize: PropTypes.number,
    zones: PropTypes.arrayOf(PropTypes.shape({
      x: PropTypes.number,
      y: PropTypes.number,
      width: PropTypes.number,
      height: PropTypes.number,
    })),
    layers: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      data: PropTypes.arrayOf(PropTypes.number),
    })),
  }),
  /** `{ p: [[id,tx,ty]], n: [[id,tx,ty]] }` from the OVERVIEW feed. */
  overview: PropTypes.shape({
    p: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
    n: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  }),
  localShortId: PropTypes.number,
  large: PropTypes.bool,
  /** Called with foot-space world coordinates when the map is clicked. */
  onWalkTo: PropTypes.func,
};

Minimap.defaultProps = {
  map: null, overview: null, localShortId: null, large: false, onWalkTo: null,
};

export default Minimap;
